// src/store/selectors.ts
import { useConfigStore } from "./index";
import {
  CatalogCategory,
  CatalogItem,
  GROUP_RELATIONS,
  GroupBucket,
  MainCategoriesEnum,
  ResolvedSelection,
  ProductConfig,
  getMainCategoryInfo,
  CategorySelectionState,
  CatalogByCategory,
  ProductNameEnum,
  TextureCategory,
  ExtraCategory, UID
} from "@shared/core";
import { groupByRelation } from "../utils/groupByRelation";
import { useMemo } from "react";
import { resolveSelectedGroupForCategory } from "../utils/common";
import { getItemPrice } from "../utils/formatPrice";
import { VAT } from "../constants";

/**
 * Catalog (data source) for the currently active product.
 * This shape does NOT depend on the new per-product products format.
 */
export function useActiveCatalog() {
  return useConfigStore((s) => s.catalog);
}

/**
 * Currently active product id.
 */
export function useActiveProduct() {
  return useConfigStore((s) => s.activeProduct);
}

/**
 * Currently active main category (HULL, FABRICS, etc.)
 */
export function useActiveCategory() {
  return useConfigStore((s) => s.activeMainCategory);
}

/**
 * Full per-product node: { configs, ui } for the active product.
 */
export function useActiveProductNode() {
  const product = useActiveProduct();
  return useConfigStore((s) => s.products[product]);
}

/**
 * Only the ProductConfig for the active product.
 * Falls back to empty object if nothing is stored yet.
 */
export function useActiveProductConfig(): ProductConfig {
  const product = useActiveProduct();
  return useConfigStore(
    (s) => s.products[product]?.config ?? ({} as ProductConfig)
  );
}

/**
 * Only the UI/meta node for the active product (placeholder for future fields).
 */
export function useActiveProductUi() {
  const product = useActiveProduct();
  return useConfigStore((s) => s.products[product]?.ui);
}

/**
 * Group buckets for texture categories, based on GROUP_RELATIONS.
 */
export function useGroupBuckets(category: MainCategoriesEnum) {
  const catalog = useActiveCatalog();
  const cat = catalog[category];
  // if (!cat || cat.type !== "texture") return [] as GroupBucket[];
  if (!cat) return [] as GroupBucket[];
  return groupByRelation(cat.items, GROUP_RELATIONS);
}

export function useMainCategoryStaticData(): CatalogCategory {
  const product = useActiveProduct();
  const activeCategory = useActiveCategory();
  return useMemo(() => getMainCategoryInfo(product, activeCategory), [product, activeCategory])
}

/**
 * Current active "group" (Metallic / Classic / etc.) for a texture category.
 * 1. Prefer explicit sel.group if present in config.
 * 2. Otherwise infer from selected uid's item.group.
 * 3. Fallback to the first item that has a group.
 */
/*@TODO Does not work properly*/
export function useActiveGroup(category: MainCategoriesEnum) {
  const catalog = useActiveCatalog();
  const product = useActiveProduct();

  // NOTE: new products structure: products[product] -> { products, ui }
  const sel = useConfigStore(
    (s) => s.products[product]?.config?.[category]
  );

  const cat = catalog[category];
  if (!cat || cat.type !== "texture") return undefined;

  // explicit group stored in config
  if (sel?.group) return (sel as any).group as string;

  // infer from selected item
  if (sel?.kind === "single" && sel.uid) {
    const it = cat.items.find((i) => i.uid === sel.uid);
    if (it?.group) return it.group;
  }

  // fallback: first item that has any group
  return cat.items.find((i) => (i as any).group)?.group;
}

export function useRawSelection(
  category: MainCategoriesEnum
): CategorySelectionState | undefined {
  return useConfigStore((s) => {
    const product = s.activeProduct as ProductNameEnum;
    const productNode = s.products?.[product];
    return productNode?.config?.[category] as CategorySelectionState | undefined;
  });
}

export function useSelection(
  category: MainCategoriesEnum
): CategorySelectionState | undefined {
  const catalog = useActiveCatalog() as CatalogByCategory;
  const productNode = useActiveProductNode(); // products[activeProduct]
  const rawSelection = useRawSelection(category);

  const cat = catalog?.[category] as CatalogCategory | undefined;
  if (!cat) return rawSelection;

  const selectedGroup = resolveSelectedGroupForCategory(
    productNode?.ui as any,
    category,
    catalog
  );

  if (cat.type !== "texture" || !selectedGroup) {
    return rawSelection;
  }

  const groupItems = cat.items.filter((i) => i.group === selectedGroup);
  const fallbackItem = groupItems[0] ?? cat.items[0];

  if (!rawSelection) {
    return fallbackItem
      ? ({ kind: "single", uid: fallbackItem.uid } as CategorySelectionState)
      : undefined;
  }

  if (rawSelection.kind === "single") {
    const current = cat.items.find((i) => i.uid === rawSelection.uid);
    if (!current || current.group !== selectedGroup) {
      return fallbackItem
        ? ({ kind: "single", uid: fallbackItem.uid } as CategorySelectionState)
        : undefined;
    }
    return rawSelection;
  }

  if (rawSelection.kind === "multi") {
    const allowedUids = new Set(groupItems.map((i) => i.uid));
    const filtered = rawSelection.uids.filter((uid) => allowedUids.has(uid));

    if (filtered.length === 0) {
      return fallbackItem
        ? ({ kind: "single", uid: fallbackItem.uid } as CategorySelectionState)
        : undefined;
    }

    return { kind: "multi", uids: filtered } as CategorySelectionState;
  }

  return rawSelection;
}
/**
 * Shortcut: selection for the currently active main category.
 */
export function useCurrentSelection() {
  const activeCategory = useConfigStore((s) => s.activeMainCategory);
  return useSelection(activeCategory);
}

export function useSelectedGroup(category: MainCategoriesEnum) {
  const product = useActiveProduct();
  return useConfigStore(
    (s) => s.products[product]?.ui.selectedGroup?.[category] ?? null
  );
}

export function useActiveBucket(
  category: MainCategoriesEnum
): GroupBucket /*| null*/ {
  const selectedGroup = useSelectedGroup(category);
  const grouped = useGroupBuckets(category);

  return useMemo(() => {
    // if (!grouped.length) return null;

    if (!selectedGroup) return grouped[0];

    return grouped.find((b) => b.relation.uid === selectedGroup) ?? grouped[0];
  }, [grouped, selectedGroup]);
}

export function useTotalPrice(activeProduct: ProductNameEnum, { basePrice = 0 } = {}) {
  const configData = useConfigStore(s => s.products[activeProduct]);
  const catalog = useActiveCatalog();

  const itemsExc= useMemo(() => {
    let itemsCost = 0;

    /* @ts-ignore */
    Object.keys(configData?.config).forEach((categoryKey) => {
      /* @ts-ignore */
      const cfgCategory = configData?.config[categoryKey];
      /* @ts-ignore */
      const catalogCategory = catalog[categoryKey];

      if (!catalogCategory) return;

      if (cfgCategory.kind === "single") {
        itemsCost += getItemPrice(catalogCategory, cfgCategory.uid);
      } else if (cfgCategory.kind === "multi") {
        cfgCategory.uids.forEach((uid: UID) => {
          itemsCost += getItemPrice(catalogCategory, uid);
        });
      }
    });
    return itemsCost;
  }, [configData, catalog]);

  const itemsInc = Math.round(itemsExc * VAT);

  return itemsInc + basePrice
}
